import { Injectable } from '@angular/core';

/**
 * Helper service for Disponibilidad General feature
 * Handles date bucket building, service categorization, and unit type counting
 * Migrated from trosten-corp-school disponibilidad.js service
 */
@Injectable()
export class DisponibilidadGeneralHelperService {

    constructor() { }

    /**
     * Pads single digit numbers with leading zero
     */
    private pad2(n: number): string {
        return n < 10 ? '0' + n : '' + n;
    }

    /**
     * Converts millisecond timestamp to YYYY-MM-DD date key
     */
    private dateKey(ms: number): string {
        const d = new Date(ms);
        const y = d.getFullYear();
        const m = this.pad2(d.getMonth() + 1);
        const day = this.pad2(d.getDate());
        return y + '-' + m + '-' + day;
    }

    /**
     * Returns all unit types that should be displayed in the table
     */
    public getAllUnitTypes(): string[] {
        return [
            'TIPO 1',
            'TIPO 2',
            'TIPO 2 LUJO',
            'TIPO 3 (21 PAS)',
            'AUTOS',
            'CLASE V SIENA',
            'V CLASS LUJO XL',
            'SUBURBAN'
        ];
    }

    /**
     * Maps Firestore unit type names to threshold key names
     * @param firestoreUnitType - Unit type from Firestore servicios collection
     * @returns Threshold key name or null if no threshold applies
     */
    public mapUnitTypeToThresholdKey(firestoreUnitType: string): string {
        if (!firestoreUnitType) {
            return null;
        }

        const lower = firestoreUnitType.toLowerCase();

        // Handle vehículo types - all map to AUTOS
        if (lower.includes('vehículo') || lower.includes('vehiculo')) {
            return 'AUTOS';
        }

        // Camioneta Tipo 1
        if (lower.includes('camioneta tipo 1')) {
            return 'TIPO 1';
        }

        // Camioneta Tipo 2 (check for Lujo first)
        if (lower.includes('camioneta tipo 2')) {
            if (lower.includes('lujo')) {
                return 'TIPO 2 LUJO';
            }
            return 'TIPO 2';
        }

        // Camioneta Tipo 3
        if (lower.includes('camioneta tipo 3')) {
            return 'TIPO 3 (21 PAS)';
        }

        // Van Ejecutiva Lujo Extra Larga
        if (lower.includes('van ejecutiva') && lower.includes('lujo') && lower.includes('extra larga')) {
            return 'V CLASS LUJO XL';
        }

        // Van Ejecutiva (Lujo or Básica) - maps to CLASE V SIENA
        if (lower.includes('van ejecutiva')) {
            return 'CLASE V SIENA';
        }

        // Suburban
        if (lower.includes('suburban')) {
            return 'SUBURBAN';
        }

        // Solo Host / Solo Operador - no threshold
        if (lower.includes('solo host') || lower.includes('solo operador')) {
            return null;
        }

        return null;
    }

    /**
     * Builds date buckets for the given range
     * Each bucket represents one day and contains:
     * - timestamp: midnight timestamp for the day
     * - services: array of services for that day
     * - solicitudes: array of solicitudes for that day
     *
     * @param fromMs - Start timestamp (inclusive)
     * @param toMs - End timestamp (inclusive)
     * @returns Object with date keys (YYYY-MM-DD) mapping to bucket data
     */
    public buildDateBuckets(fromMs: number, toMs: number): any {
        const buckets: any = {};
        let current = new Date(fromMs);
        current.setHours(0, 0, 0, 0);

        const end = new Date(toMs);
        end.setHours(0, 0, 0, 0);

        while (current <= end) {
            const key = this.dateKey(current.getTime());
            buckets[key] = {
                timestamp: current.getTime(),
                services: [],
                solicitudes: []
            };

            current.setDate(current.getDate() + 1);
        }

        return buckets;
    }

    /**
     * Categorizes servicios into date buckets based on start/end dates
     * Each service may appear in multiple buckets (start day, ongoing days, end day)
     *
     * @param buckets - Date buckets object from buildDateBuckets()
     * @param servicios - Array of servicios from Firestore
     */
    public categorizeServiciosIntoBuckets(buckets: any, servicios: any[]): void {
        if (!servicios || servicios.length === 0) {
            return;
        }

        servicios.forEach(function(servicio) {
            const inicioMs = servicio.inicioDeServicio;
            const finMs = servicio.finDeServicio || inicioMs;

            const inicioDate = new Date(inicioMs);
            inicioDate.setHours(0, 0, 0, 0);
            const inicioKey = this.dateKey(inicioDate.getTime());

            const finDate = new Date(finMs);
            finDate.setHours(0, 0, 0, 0);
            const finKey = this.dateKey(finDate.getTime());

            // Iterate through all buckets and determine relationship
            for (const key in buckets) {
                if (buckets.hasOwnProperty(key)) {
                    const bucketDate = new Date(buckets[key].timestamp);
                    bucketDate.setHours(0, 0, 0, 0);
                    const bucketKey = this.dateKey(bucketDate.getTime());

                    // Determine if service starts, is ongoing, or ends on this day
                    let serviceType = null;

                    if (bucketKey === inicioKey && bucketKey === finKey) {
                        // Single-day service - only count as 'start' to match corp-school behavior
                        serviceType = 'start';
                    } else if (bucketKey === inicioKey) {
                        // Service starts on this day
                        serviceType = 'start';
                    } else if (bucketKey === finKey) {
                        // Service ends on this day
                        serviceType = 'end';
                    } else if (bucketDate > inicioDate && bucketDate < finDate) {
                        // Service is ongoing on this day
                        serviceType = 'ongoing';
                    }

                    if (serviceType) {
                        buckets[key].services.push({
                            ...servicio,
                            _serviceType: serviceType
                        });
                    }
                }
            }
        }.bind(this));
    }

    /**
     * Categorizes solicitudes into date buckets
     * Similar to categorizeServiciosIntoBuckets but for solicitudes
     *
     * @param buckets - Date buckets object from buildDateBuckets()
     * @param solicitudes - Array of solicitudes from PHP endpoint
     */
    public categorizeSolicitudesIntoBuckets(buckets: any, solicitudes: any[]): void {
        if (!solicitudes || solicitudes.length === 0) {
            return;
        }

        solicitudes.forEach(function(solicitud) {
            if (!solicitud.info_cotizacion || !solicitud.info_cotizacion.fechaInicio) {
                return;
            }

            const inicioMs = solicitud.info_cotizacion.fechaInicio;
            const finMs = solicitud.info_cotizacion.fechaFin || inicioMs;

            const inicioDate = new Date(inicioMs);
            inicioDate.setHours(0, 0, 0, 0);
            const inicioKey = this.dateKey(inicioDate.getTime());

            const finDate = new Date(finMs);
            finDate.setHours(0, 0, 0, 0);
            const finKey = this.dateKey(finDate.getTime());

            // Iterate through all buckets
            for (const key in buckets) {
                if (buckets.hasOwnProperty(key)) {
                    const bucketDate = new Date(buckets[key].timestamp);
                    bucketDate.setHours(0, 0, 0, 0);
                    const bucketKey = this.dateKey(bucketDate.getTime());

                    let serviceType = null;

                    if (bucketKey === inicioKey && bucketKey === finKey) {
                        serviceType = 'start_and_end';
                    } else if (bucketKey === inicioKey) {
                        serviceType = 'start';
                    } else if (bucketKey === finKey) {
                        serviceType = 'end';
                    } else if (bucketDate > inicioDate && bucketDate < finDate) {
                        serviceType = 'ongoing';
                    }

                    if (serviceType) {
                        buckets[key].solicitudes.push({
                            ...solicitud,
                            _serviceType: serviceType
                        });
                    }
                }
            }
        }.bind(this));
    }

    /**
     * Counts units by type for a given array of services
     * Returns breakdown of start/ongoing/end counts
     *
     * @param services - Array of services (from a bucket)
     * @param unitType - Unit type to count (e.g., 'TIPO 2', 'AUTOS')
     * @returns Object with start, ongoing, end, and total counts
     */
    public countUnitsByType(services: any[], unitType: string): any {
        const counts = {
            start: 0,
            ongoing: 0,
            end: 0,
            total: 0
        };

        if (!services || services.length === 0) {
            return counts;
        }

        services.forEach(function(servicio) {
            // Get the unit type from servicio
            let servicioUnitType = null;

            // Check if servicio has tipoDeUnidad or combinaciones
            if (servicio.tipoDeUnidad) {
                servicioUnitType = this.mapUnitTypeToThresholdKey(servicio.tipoDeUnidad);
            } else if (servicio.combinaciones && servicio.combinaciones.length > 0) {
                // If multiple combinations, count each one
                servicio.combinaciones.forEach(function(combo: any) {
                    if (combo.tipoDeUnidad) {
                        const comboUnitType = this.mapUnitTypeToThresholdKey(combo.tipoDeUnidad);
                        if (comboUnitType === unitType) {
                            const cantidad = combo.cantidad || 1;
                            counts.total += cantidad;

                            // Categorize by service type
                            if (servicio._serviceType === 'start') {
                                counts.start += cantidad;
                            }
                            if (servicio._serviceType === 'ongoing') {
                                counts.ongoing += cantidad;
                            }
                            if (servicio._serviceType === 'end') {
                                counts.end += cantidad;
                            }
                        }
                    }
                }.bind(this));
                return; // Already processed combinations
            } else if (servicio.unidadesSolicitadas) {
                // Check unidadesSolicitadas object (key-value pairs)
                for (const unidadKey in servicio.unidadesSolicitadas) {
                    if (servicio.unidadesSolicitadas.hasOwnProperty(unidadKey)) {
                        const cantidad = servicio.unidadesSolicitadas[unidadKey];
                        if (cantidad > 0) {
                            const mappedType = this.mapUnitTypeToThresholdKey(unidadKey);

                            if (mappedType === unitType) {
                                counts.total += cantidad;

                                // Categorize by service type
                                if (servicio._serviceType === 'start') {
                                    counts.start += cantidad;
                                }
                                if (servicio._serviceType === 'ongoing') {
                                    counts.ongoing += cantidad;
                                }
                                if (servicio._serviceType === 'end') {
                                    counts.end += cantidad;
                                }
                            }
                        }
                    }
                }
                return; // Already processed unidadesSolicitadas
            }

            // Single unit type match (legacy path)
            if (servicioUnitType === unitType) {
                counts.total += 1;

                if (servicio._serviceType === 'start') {
                    counts.start += 1;
                }
                if (servicio._serviceType === 'ongoing') {
                    counts.ongoing += 1;
                }
                if (servicio._serviceType === 'end') {
                    counts.end += 1;
                }
            }
        }.bind(this));

        return counts;
    }

    /**
     * Counts units by type for solicitudes
     * Similar to countUnitsByType but for solicitudes structure
     *
     * @param solicitudes - Array of solicitudes (from a bucket)
     * @param unitType - Unit type to count
     * @returns Object with start, ongoing, end, and total counts
     */
    public countSolicitudesByType(solicitudes: any[], unitType: string): any {
        const counts = {
            start: 0,
            ongoing: 0,
            end: 0,
            total: 0
        };

        if (!solicitudes || solicitudes.length === 0) {
            return counts;
        }

        solicitudes.forEach(function(solicitud) {
            if (!solicitud.parejas || solicitud.parejas.length === 0) {
                return;
            }

            // Iterate through parejas (unit combinations)
            solicitud.parejas.forEach(function(pareja: any) {
                if (pareja.tipoDeUnidad) {
                    const parejaUnitType = this.mapUnitTypeToThresholdKey(pareja.tipoDeUnidad);
                    if (parejaUnitType === unitType) {
                        const cantidad = pareja.cantidad || 1;
                        counts.total += cantidad;

                        if (solicitud._serviceType === 'start') {
                            counts.start += cantidad;
                        }
                        if (solicitud._serviceType === 'ongoing') {
                            counts.ongoing += cantidad;
                        }
                        if (solicitud._serviceType === 'end') {
                            counts.end += cantidad;
                        }
                    }
                }
            }.bind(this));
        }.bind(this));

        return counts;
    }
}



// WEBPACK FOOTER //
// E:/Github/trosten/trosten-web-app/src/app/services/helpers/disponibilidad-general-helper.service.ts